<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { TotpFinalizing, TotpSetup } from '@/types'
import { STATUS_CODES } from '@/constants'
import { features } from 'caniuse-lite'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useDirtyFormGuard } from '@/composables/useDirtyFormGuard'

const props = defineProps<{
  hasTotp: boolean
}>()

const showTotpForm = ref(false)
const hasActiveTotp = ref(false)
const totpSetup = ref<TotpSetup>({} as TotpSetup)
const totpFinalizing = ref<TotpFinalizing>({} as TotpFinalizing)
const userTotp = ref('')
const emailCode = ref('')
const loading = ref(false)
const setupSuccess = ref(false)
const error = ref('')
const { t } = useI18n()

// Answers the setup and finalize calls give instead of succeeding, keyed by
// status and, where one status covers two causes, by error code.
const setupErrorMessage = (err: unknown): string | null => {
  const res = (err as { response?: { status?: number; data?: { error?: string } } }).response
  switch (res?.status) {
    case STATUS_CODES.CONFLICT:
      hasActiveTotp.value = true
      showTotpForm.value = false
      return t('dashboard.account.security.totpAlreadyEnabled')
    case STATUS_CODES.GONE:
      cancelSetup()
      return t('dashboard.account.security.setupExpired')
    case STATUS_CODES.FORBIDDEN:
      return res.data?.error === 'email_code_invalid'
        ? t('dashboard.account.security.emailCodeInvalid')
        : t('dashboard.account.security.totpInvalid')
    case STATUS_CODES.SERVICE_UNAVAILABLE:
      return t('dashboard.account.security.mailUnavailable')
    case STATUS_CODES.TOO_MANY_REQUESTS:
      return t('dashboard.account.security.tooManyAttempts')
    default:
      return null
  }
}

const showSetupError = (err: unknown, logPrefix: string) => {
  const message = setupErrorMessage(err)
  if (message) error.value = message
  else handleAxiosError(err, { logPrefix, reportNetworkErrors: false })
}

// Track dirty state - form is dirty when setup form is open and user has entered either code
const isTotpFormDirty = computed(() => {
  if (!showTotpForm.value) return false
  return !!userTotp.value.trim() || !!emailCode.value.trim()
})
useDirtyFormGuard('account-totp', isTotpFormDirty)

const requestTotpSetup = () => {
  loading.value = true
  error.value = ''
  useAxios<null, TotpSetup>('GET', '/account/totp/setup', true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const validate = TotpSetup.safeParse(res.data)
        if (!validate.success) return

        totpSetup.value = { ...validate.data }
        totpFinalizing.value = {
          req_id: validate.data.req_id,
          totp: '',
          email_code: ''
        }
        showTotpForm.value = true
      }
    })
    .catch((err) => showSetupError(err, 'AccountTotp:setup'))
    .finally(() => {
      loading.value = false
    })
}

const finalizeTotpSetup = () => {
  if (userTotp.value === '' || emailCode.value === '') return
  loading.value = true
  error.value = ''
  totpFinalizing.value.totp = userTotp.value
  totpFinalizing.value.email_code = emailCode.value

  useAxios<TotpFinalizing, null>('POST', '/account/totp/finalize', true, totpFinalizing.value)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.CREATED) {
        hasActiveTotp.value = true
        showTotpForm.value = false
        setupSuccess.value = true
        setTimeout(() => {
          setupSuccess.value = false
        }, 5000)
      }
    })
    .catch((err) => showSetupError(err, 'AccountTotp:finalize'))
    .finally(() => {
      loading.value = false
    })
}

function cancelSetup() {
  showTotpForm.value = false
  userTotp.value = ''
  emailCode.value = ''
}

watch(
  () => props.hasTotp,
  () => {
    hasActiveTotp.value = props.hasTotp
  }
)

onMounted(() => {
  hasActiveTotp.value = props.hasTotp
})
</script>

<template>
  <div class="totp-section">
    <!-- Section Header -->
    <div class="card-header">
      <div class="card-icon">
        <v-icon icon="mdi-shield-key" size="24" />
      </div>
      <div class="card-info">
        <h3 class="card-title">{{ $t('dashboard.account.security.twoFactor') }}</h3>
        <p class="card-description">{{ $t('dashboard.account.security.twoFactorDescription') }}</p>
      </div>
      <div class="card-status">
        <span v-if="hasActiveTotp" class="status-badge status-active">
          <v-icon icon="mdi-check-circle" size="16" />
          {{ $t('dashboard.account.security.activated') }}
        </span>
        <span v-else class="status-badge status-inactive">
          <v-icon icon="mdi-alert-circle" size="16" />
          {{ $t('dashboard.account.security.notActivated') }}
        </span>
      </div>
    </div>

    <!-- Success Message -->
    <Transition name="slide-fade">
      <div v-if="setupSuccess" class="alert alert-success">
        <v-icon icon="mdi-check-circle" size="20" />
        <span>{{ $t('dashboard.account.security.setupSuccess') }}</span>
      </div>
    </Transition>

    <div v-if="error" class="alert alert-error">
      <v-icon icon="mdi-alert-circle" size="20" />
      <span>{{ error }}</span>
    </div>

    <!-- TOTP Already Active -->
    <div v-if="hasActiveTotp && !showTotpForm" class="totp-active">
      <p class="active-message">
        <v-icon icon="mdi-shield-check" size="20" />
        {{ $t('dashboard.account.security.twoFactorActive') }}
      </p>
    </div>

    <!-- Setup Button -->
    <div v-else-if="!showTotpForm" class="totp-setup-prompt">
      <p class="setup-info">
        {{ $t('dashboard.account.security.twoFactorActivate') }}
      </p>
      <button class="btn btn-primary" :disabled="loading" @click="requestTotpSetup">
        <v-progress-circular v-if="loading" indeterminate size="18" width="2" />
        <v-icon v-else icon="mdi-shield-plus" size="18" />
        <span>{{ loading ? $t('dashboard.account.security.preparing') : $t('dashboard.account.security.setupOtp') }}</span>
      </button>
    </div>

    <!-- Setup Form -->
    <div v-else class="totp-setup-form">
      <div class="qr-section">
        <div class="qr-wrapper">
          <img
            :src="features.webp?.D
              ? `data:image/webp;base64,${totpSetup.webp_image}`
              : `data:image/jpg;base64,${totpSetup.jpg_image}`"
            :alt="$t('dashboard.account.security.qrCodeAlt')"
            class="qr-image"
          />
        </div>
        <div class="qr-info">
          <p class="qr-instruction">{{ $t('dashboard.account.security.scanQrCode') }}</p>
          <div class="secret-key">
            <span class="secret-label">{{ $t('dashboard.account.payments.manualKey') }}</span>
            <code class="secret-value">{{ totpSetup.secret_key }}</code>
          </div>
        </div>
      </div>

      <div class="verify-section">
        <label class="form-label">{{ $t('dashboard.account.security.emailCode') }}</label>
        <div class="verify-input-wrapper">
          <input
            v-model="emailCode"
            type="text"
            class="form-input totp-input"
            :placeholder="$t('dashboard.account.security.verificationPlaceholder')"
            maxlength="6"
            autocomplete="one-time-code"
          />
        </div>
        <p class="verify-hint">{{ $t('dashboard.account.security.emailCodeHint') }}</p>
      </div>

      <div class="verify-section">
        <label class="form-label">{{ $t('dashboard.account.security.verificationCode') }}</label>
        <div class="verify-input-wrapper">
          <input
            v-model="userTotp"
            type="text"
            class="form-input totp-input"
            :placeholder="$t('dashboard.account.security.verificationPlaceholder')"
            maxlength="6"
            autocomplete="one-time-code"
            @keydown.enter="finalizeTotpSetup"
          />
        </div>
        <p class="verify-hint">{{ $t('dashboard.account.security.verificationHint') }}</p>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-secondary" @click="cancelSetup" :disabled="loading">
          {{ $t('dashboard.account.security.cancel') }}
        </button>
        <button
          type="button"
          class="btn btn-success"
          :disabled="userTotp.length < 6 || emailCode.length < 6 || loading"
          @click="finalizeTotpSetup"
        >
          <v-progress-circular v-if="loading" indeterminate size="18" width="2" />
          <v-icon v-else icon="mdi-check" size="18" />
          <span>{{ loading ? $t('dashboard.account.security.verifying') : $t('dashboard.account.security.verify') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.totp-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Card Header */
.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 12px;
  color: #00B0FF;
  flex-shrink: 0;
}

.card-info {
  flex: 1;
}

.card-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 4px;
}

.card-description {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.card-status {
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-active {
  background: rgba(76, 175, 80, 0.1);
  color: #66bb6a;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.status-inactive {
  background: rgba(255, 152, 0, 0.1);
  color: #ffb74d;
  border: 1px solid rgba(255, 152, 0, 0.2);
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 500;
}

.alert-success {
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  color: #66bb6a;
}

.alert-error {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  color: #ef5350;
}

/* TOTP Active */
.totp-active {
  padding: 16px;
  background: rgba(76, 175, 80, 0.05);
  border: 1px solid rgba(76, 175, 80, 0.1);
  border-radius: 12px;
}

.active-message {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.7);
}

/* Setup Prompt */
.totp-setup-prompt {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.setup-info {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
  line-height: 1.6;
  margin: 0;
}

/* Setup Form */
.totp-setup-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.qr-section {
  display: flex;
  gap: 24px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
}

.qr-wrapper {
  flex-shrink: 0;
}

.qr-image {
  width: 180px;
  height: 180px;
  border-radius: 8px;
  background: white;
  padding: 8px;
}

.qr-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qr-instruction {
  font-size: 0.95rem;
  color: rgba(200, 218, 255, 0.8);
  margin: 0;
  line-height: 1.5;
}

.secret-key {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.secret-label {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.secret-value {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.9rem;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 8px;
  color: #00B0FF;
  word-break: break-all;
}

/* Verify Section */
.verify-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 300px;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #00B0FF;
    background: rgba(0, 176, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(0, 176, 255, 0.1);
  }
}

.totp-input {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 1.25rem;
  letter-spacing: 0.3em;
  text-align: center;
}

.verify-hint {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: rgba(0, 176, 255, 0.15);
  color: #00B0FF;
  border: 1px solid rgba(0, 176, 255, 0.3);

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.25);
    box-shadow: 0 0 20px rgba(0, 176, 255, 0.15);
  }
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(200, 218, 255, 0.7);
  border: 1px solid rgba(120, 163, 255, 0.15);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }
}

.btn-success {
  background: rgba(76, 175, 80, 0.15);
  color: #66bb6a;
  border: 1px solid rgba(76, 175, 80, 0.3);

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.15);
  }
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Light Theme */
.v-theme--lightTheme {
  .card-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .card-description,
  .setup-info,
  .active-message,
  .qr-instruction {
    color: rgba(15, 23, 42, 0.6);
  }

  .card-icon {
    background: rgba(28, 110, 227, 0.1);
    border-color: rgba(28, 110, 227, 0.2);
    color: #1c6ee3;
  }

  .qr-section {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.08);
  }

  .totp-active {
    background: rgba(76, 175, 80, 0.03);
    border-color: rgba(76, 175, 80, 0.1);
  }

  .secret-value {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.1);
    color: #1c6ee3;
  }

  .form-input {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.95);

    &:focus {
      border-color: #1c6ee3;
      background: rgba(28, 110, 227, 0.03);
      box-shadow: 0 0 0 3px rgba(28, 110, 227, 0.1);
    }
  }

  .form-label,
  .secret-label {
    color: rgba(15, 23, 42, 0.6);
  }

  .verify-hint {
    color: rgba(15, 23, 42, 0.4);
  }

  .btn-primary {
    background: rgba(28, 110, 227, 0.1);
    color: #1c6ee3;
    border-color: rgba(28, 110, 227, 0.2);
  }

  .btn-secondary {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(15, 23, 42, 0.7);
    border-color: rgba(28, 110, 227, 0.15);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .card-header {
    flex-wrap: wrap;
  }

  .card-status {
    width: 100%;
    margin-top: 8px;
  }

  .qr-section {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .form-actions {
    flex-direction: column;

    .btn {
      width: 100%;
      justify-content: center;
    }
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .form-input,
  .btn,
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: none;
  }
}
</style>
